<template>
  <div class="card boxWithBorder">
    <p class="modal-card-title gant-text mb-5">Tell Us About Your Trip</p>
    

    <div class="mb-5">
      <div class="label">Who is traveling?</div>
      <b-field>
        <b-radio
          v-model="bookingFor"
          name="user"
          native-value="user"
          @change="onBookingForChange"
        >
          Me ({{ currentUser.firstName }} {{ currentUser.lastName }})
        </b-radio>
        <b-radio
          v-model="bookingFor"
          name="others"
          native-value="others"
          @change="onBookingForChange"
        >
          Someone else
        </b-radio>
      </b-field>
    </div>

    <!-- Add Guest Modal -->
    <b-modal
      v-model="isGuestModalActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      aria-role="dialog"
      aria-label="Add Guest Traveler"
      aria-modal
    >
      <template #default="props">
        <TravellerAddGuest
          v-bind="profiler"
          @close="props.close"
          @update="onGuestAdded"
        />
      </template>
    </b-modal>

    <!-- Other travelers selection -->
    <div v-if="bookingFor === 'others'">

      <!-- Loading overlay -->
      <b-loading :is-full-page="false" v-model="isLoadingMyTravelers"></b-loading>

      <b-field
        label="Book for one of my travelers"
        label-position="on-border"
        :key="forceKey"
      >
        <TagAutocomplete
          :onlyOne="true"
          placeholder="e.g. John Smith"
          icon="magnify"
          :chainData="formattedTravelers"
          :selected="selectedTravelers"
          @list="onTravelerSelected"
        />
      </b-field>
      
      <div v-if="selectedTravelers.length === 0">
        <a @click="openGuestModal" class="has-text-primary">
          <u>Book for a guest traveler</u>
        </a>
      </div>
    </div>

    <!-- Trip type selection -->
    <div class="mt-5 mb-5" v-if="showTripTypeSelection">
      <div class="label">Is this a business or personal trip?</div>
      <b-field>
        <b-radio
          v-model="tripType"
          name="business"
          native-value="business"
        >
          Business
        </b-radio>
        <b-radio
          v-model="tripType"
          name="personal"
          native-value="personal"
        >
          Personal
        </b-radio>
      </b-field>
    </div>

    <b-button
      :disabled="!canSave"
      :loading="isSaving"
      label="Save"
      @click="handleSave"
      type="is-primary"
      expanded
    />
  </div>
</template>

<script>
import TagAutocomplete from "../Tags/TagAutocomplete.vue"
import TravellerAddGuest from "./TravellerAddGuest.vue"

export default {
  name: "TravellerModal",
  
  components: {
    TagAutocomplete,
    TravellerAddGuest,
  },
  
  props: {
    profiler: {
      type: Object,
      default: () => ({})
    },
    mounted: {
      type: Boolean,
      default: false
    },
    closable: {
      type: Boolean,
      default: true
    },
  },
  
  data() {
    return {
      dependentTravelers: [],
      selectedTravelers: [],
      isGuestModalActive: false,
      // isLoading: false,
      isLoadingMyTravelers: false,
      isSaving: false,
      forceKey: 0,
    }
  },
  
  computed: {
    currentUser() {
      return this.$store.state.travellersAndTrips.profile || {}
    },
    
    bookingFor: {
      get() {
        return this.$globals.travellers.bookFor
      },
      set(value) {
        this.$globals.travellers.bookFor = value
      }
    },
    
    tripType: {
      get() {
        return this.$globals.travellers.tripType
      },
      set(value) {
        this.$globals.travellers.tripType = value
      }
    },
    
    formattedTravelers() {
      return this.dependentTravelers.map(traveler => [
        traveler,
        this.formatTravelerName(traveler)
      ])
    },
    
    canSave() {
      return this.selectedTravelers.length > 0 || this.bookingFor === "user"
    },
    
    showTripTypeSelection() {
      if (!this.canSave) return false
      
      const profile = this.getActiveProfile()
      return profile.account?.allowPersonalTravel || false
    },
    
    hasExistingBooking() {
      const result = this.$store.state.bookTravel.result
      return !this.$helpers.isEmptyObject(result.car) ||
             !this.$helpers.isEmptyObject(result.flight) ||
             !this.$helpers.isEmptyObject(result.hotel)
    }
  },
  
  methods: {
    async loadMyTravelers() {
      if (this.isLoadingMyTravelers) return

      this.isLoadingMyTravelers = true

      try {
        const result = await this.$app.huddleLabsServerCall("profile/my-travelers", { contactId: this.$globals.profile.id })
        
        if (result.error) {
          throw new Error(result.message || 'Failed to load travelers')
        }
        
        this.dependentTravelers = result.data || []
        this.forceKey++
        
      } catch (error) {
        console.error('Error loading dependent travelers:', error)
        this.$buefy.toast.open({
          message: 'Unable to load your travelers. Please try again.',
          type: 'is-danger'
        })
      } finally {
        this.isLoadingMyTravelers = false
      }
    },
    
    formatTravelerName(traveler) {
      const middle = traveler.middleName ? `${traveler.middleName[0]}. ` : ''
      return `${traveler.firstName} ${middle}${traveler.lastName}`
    },
    
    getActiveProfile() {
      if (this.bookingFor === 'others' && this.selectedTravelers.length > 0) {
        return this.selectedTravelers[0][0]
      }
      return this.currentUser
    },
    
    onBookingForChange() {
      // Clear selected travelers when switching booking type
      this.selectedTravelers = []
    },
    
    onTravelerSelected(selection) {
      this.selectedTravelers = selection
    },
    
    openGuestModal() {
      this.isGuestModalActive = true
    },
    
    async onGuestAdded(guestData) {
      this.isGuestModalActive = false
      if (guestData) {
        await this.loadMyTravelers()
        // Auto-select the newly added guest if possible
        const newGuest = this.dependentTravelers.find(t => t.id === guestData.id)
        if (newGuest) {
          this.selectedTravelers = [[newGuest, this.formatTravelerName(newGuest)]]
        }
      }
    },
    
    async handleSave() {
      if (this.$globals.travellers.set && this.hasExistingBooking) {
        await this.confirmTravelerChange()
      } else {
        await this.saveTravelers()
      }
    },
    
    async confirmTravelerChange() {
      return new Promise((resolve) => {
        this.$buefy.dialog.confirm({
          message: `You have travel in your itinerary that is linked to another traveller's policy. Changing travellers now will delete this trip. Do you wish to continue?`,
          onConfirm: async () => {
            this.$buefy.toast.open("Travellers changed")
            this.$store.commit("clearState/CLEAR_DETAILS")
            await this.saveTravelers()
            resolve()
          },
          onCancel: () => resolve()
        })
      })
    },
    
    async saveTravelers() {
      this.isSaving = true
      
      try {
        // Set global travelers state
        this.$globals.travellers.set = true
        this.$store.commit("travellersAndTrips/EDIT_TRAVELLERS", this.$globals.travellers)
        
        // Determine which profile to set as active
        let activeProfile
        if (this.bookingFor === 'others' && this.selectedTravelers.length > 0) {
          activeProfile = await this.loadOtherTravelerProfile()
        } else {
          activeProfile = this.currentUser
        }
        
        // Set the active traveler profile
        this.$store.commit('travellersAndTrips/SET_TRAVELLER_PROFILE', activeProfile)

        // Log the event
        this.$app.logEvent({
          event_type: 'Booking Started',
          event_properties: {
            booking_for: this.bookingFor,
            trip_type: this.tripType,
            traveler_count: this.selectedTravelers.length || 1,
            active_traveler_id: activeProfile.id,
            is_meeting_mode: this.pathMixin_isMeeting,
          }
        });
        
        // Clear existing booking state
        await Promise.all([
          this.$store.dispatch('bookTravel/hDelResult'),
          this.$store.dispatch('bookTravel/cDelResult'),
          this.$store.dispatch('bookTravel/delResult'),
          this.$store.dispatch('bookTravel/setAtcDetails', {})
        ])
        
        this.$emit('close')
        this.$nav.pushRouteTo({ name: 'BookingLetsGetGoing' })
        
      } catch (error) {
        console.error('Error saving travelers:', error)
        this.$buefy.toast.open({
          message: 'Unable to save traveler information. Please try again.',
          type: 'is-danger'
        })
      } finally {
        this.isSaving = false
      }
    },
    
    async loadOtherTravelerProfile() {
      const travelerId = this.selectedTravelers[0][0].id
      
      // Check if profile is already loaded
      const existingProfile = this.$store.state.travellersAndTrips.otherProfiles[travelerId]
      
      if (existingProfile) {
        return existingProfile
      }
      
      // Profile doesn't exist, load it from API
      const body = {
        contactId: travelerId,
        includeRelated: [
          'travelerPrograms',
          'paymentMethods',
          'unusedTickets',
          'travelPolicy',
          'preferredVendors',
        ],
      }
      
      const result = await this.$app.huddleLabsServerCall('profile/travel-profile', body)
      
      if (result.error) {
        throw new Error('Failed to load traveler profile')
      }
      
      // Store the profile in the store
      this.$store.commit('travellersAndTrips/ADD_OTHER_PROFILE', [
        travelerId,
        result.data
      ])
      
      return result.data
    }
  },
  
  async mounted() {
    await this.loadMyTravelers()
  }
}
</script>

<style scoped>
.card {
  position: relative;
}
</style>